import { d as defaultConfig } from './default-ui-config-e175b24a.js';
import { l as lodash_merge } from './index-96b0df1d.js';

const spacingScale = [
	0,
	0.5,
	1,
	1.5,
	2,
	3,
	4,
	5,
	6,
	7,
	8,
	9,
	10,
	12,
	14,
	16,
	20,
	24,
	28,
	32,
	36,
	40,
	44,
	48,
	52,
	56,
	60,
	64,
	72,
	80,
	96
];

const THEMES = {
  darkest: {
    background: {
      1000: '#080808',
      900: '#1A1A1A',
      800: '#1E1E1E',
      700: '#2C2C2C',
      600: '#393939',
    },
    text: '#FFFFFF',
  },
  dark: {
    background: {
      1000: '#252525',
      900: '#2F2F2F',
      800: '#323232',
      700: '#3E3E3E',
      600: '#4A4A4A',
    },
    text: '#F5F5F5',
    'video-bg': '#1C1C1C',
  },
  light: {
    background: {
      1000: '#FFFFFF',
      900: '#F5F5F5',
      800: '#EBEBEB',
      700: '#E0E0E0',
      600: '#D6D6D6',
    },
    text: '#111111',
    'video-bg': '#DADADA',
  },
};

const BORDER_WIDTHS = {
  none: {
    none: 0,
    sm: 0,
    md: 0,
    lg: 0,
  },
  thin: {
    none: 0,
    sm: 1,
    md: 2,
    lg: 4,
  },
  fat: {
    none: 0,
    sm: 2,
    md: 4,
    lg: 8,
  },
};
const BORDER_RADII = {
  sharp: {
    none: 0,
    sm: 0,
    md: 0,
    lg: 0,
  },
  rounded: {
    none: 0,
    sm: 4,
    md: 8,
    lg: 12,
  },
  'extra-rounded': {
    none: 0,
    sm: 8,
    md: 16,
    lg: 24,
  },
  circular: {
    none: 9999,
    sm: 9999,
    md: 9999,
    lg: 9999,
  },
};

const BRAND_SHADE_REDUCER = [-2, -1, 0, 1, 2];
function hexToRGB(h) {
  let r = '0', g = '0', b = '0';
  if (h.length == 4) {
    r = '0x' + h[1] + h[1];
    g = '0x' + h[2] + h[2];
    b = '0x' + h[3] + h[3];
  }
  else if (h.length == 7) {
    r = '0x' + h[1] + h[2];
    g = '0x' + h[3] + h[4];
    b = '0x' + h[5] + h[6];
  }
  else {
    throw new Error('[dyte] Invalid hex color code provided');
  }
  return [+r, +g, +b];
}
/**
 * Converts an HSL color value to RGB. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 255].
 *
 * @param   {number}  h       The hue
 * @param   {number}  s       The saturation
 * @param   {number}  l       The lightness
 * @returns {Array}           The RGB representation
 */
const hslToRgb = (h, s, l) => {
  let r, g, b;
  if (s == 0) {
    r = g = b = l; // achromatic
  }
  else {
    const hue2rgb = (p, q, t) => {
      if (t < 0)
        t += 1;
      if (t > 1)
        t -= 1;
      if (t < 1 / 6)
        return p + (q - p) * 6 * t;
      if (t < 1 / 2)
        return q;
      if (t < 2 / 3)
        return p + (q - p) * (2 / 3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};
/**
 * Converts an RGB color value to HSL. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes r, g, and b are contained in the set [0, 255] and
 * returns h, s, and l in the set [0, 1].
 *
 * @param   {number}  r       The red color value
 * @param   {number}  g       The green color value
 * @param   {number}  b       The blue color value
 * @returns {Array}           The HSL representation
 */
const rgbToHsl = (r, g, b) => {
  (r /= 255), (g /= 255), (b /= 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  if (max == min) {
    h = s = 0; // achromatic
  }
  else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }
  return [h, s, l];
};
const rgbToHex = (r, g, b) => {
  const hex = (n) => n.toString(16).padStart(2, '0');
  return `#${hex(r)}${hex(g)}${hex(b)}`;
};
/**
 * Generate shades from a single base hex color code.
 *
 * `weight` is contained in the set [0, 1].
 *
 * @param   {string} baseHexCode The base hex code to use to generate the other shades
 * @param   {number} weight The weight applied when calculating each shade.
 * @param   {number[]} reducer The reducer array which tells how to calculate the shades
 * @returns {string[]} The generated hex shades
 */
const generateShades = (baseHexCode, reducer = BRAND_SHADE_REDUCER, weight = 0.4) => {
  const shades = [];
  const [r, g, b] = hexToRGB(baseHexCode);
  const [h, s, l] = rgbToHsl(r, g, b);
  const lightness = Math.round(l * 100);
  if (lightness > 70) {
    // increase quotient for light base shade
    weight = 0.8;
  }
  else if (lightness > 60) {
    weight = 0.9;
  }
  else if (lightness < 10) {
    // reduce quorient for dark base shade
    weight = 0.075;
  }
  else if (lightness < 42) {
    weight = 0.3;
  }
  const basePosition = reducer.findIndex((val) => val === 0);
  if (basePosition === -1) {
    throw new Error('Invalid reducer provided, it must contain atleast one zero');
  }
  const lightShades = 5 - basePosition;
  const darkShades = basePosition + 1;
  const lightShadeIncrement = (100 - lightness) / lightShades;
  const darkShadeIncrement = lightness / darkShades;
  for (const val of reducer) {
    let shadeLightness;
    if (val < 0) {
      shadeLightness = lightness + val * darkShadeIncrement * weight;
    }
    else if (val > 0) {
      shadeLightness = lightness + val * lightShadeIncrement * weight;
    }
    else {
      shadeLightness = lightness;
    }
    const [r, g, b] = hslToRgb(h, s, shadeLightness / 100);
    shades.push(rgbToHex(r, g, b));
  }
  return shades;
};
const isValidHexColor = (color) => {
  return typeof color === 'string' && color.length === 7 && color.startsWith('#');
};
const getBrandColors = (shade) => {
  const [s300, s400, s500, s600, s700] = generateShades(shade, BRAND_SHADE_REDUCER);
  return { 300: s300, 400: s400, 500: s500, 600: s600, 700: s700 };
};
/**
 * Generates new background colors which are compatible with older preset color values
 * @param shade The hex color code
 * @returns Background color tokens
 */
const getBackgroundColorsOld = (shade) => {
  const [s1000, s900, s800, s700, s600] = generateShades(shade, BRAND_SHADE_REDUCER);
  return { 1000: s1000, 900: s900, 800: s800, 700: s700, 600: s600 };
};

const provideGoogleFont = (fontFamily) => {
  const weights = [400, 500, 700];
  const links = [];
  if (document.querySelector('link[data-dyte-font]') == null) {
    /**
     * Adds the following preconnect link tags for faster google font loading
     * <link rel="preconnect" href="https://fonts.googleapis.com">
     * <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
     */
    const p1 = document.createElement('link');
    p1.rel = 'preconnect';
    p1.setAttribute('data-dyte-font', 'true');
    p1.href = 'https://fonts.googleapis.com';
    const p2 = p1.cloneNode(true);
    p2.href = 'https://fonts.gstatic.com';
    p2.crossOrigin = '';
    links.push(p1, p2);
  }
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.setAttribute('data-dyte-font', fontFamily);
  link.href = encodeURI(`https://fonts.googleapis.com/css2?family=${fontFamily}:wght@${weights.join(';')}&display=swap`);
  links.push(link);
  document.head.append(...links);
};
const provideSpacing = (el, spacingBase) => {
  spacingScale.forEach((space) => {
    el.style.setProperty(`--dyte-space-${space}`, `${space * spacingBase}px`);
  });
};
const provideBorderWidth = (el, borderWidth) => {
  if (borderWidth in BORDER_WIDTHS) {
    const widths = BORDER_WIDTHS[borderWidth];
    Object.keys(widths).forEach((type) => {
      el.style.setProperty(`--dyte-border-width-${type}`, `${widths[type]}px`);
    });
  }
};
const provideBorderRadius = (el, borderRadius) => {
  if (borderRadius in BORDER_RADII) {
    const radii = BORDER_RADII[borderRadius];
    Object.keys(radii).forEach((type) => {
      el.style.setProperty(`--dyte-border-radius-${type}`, `${radii[type]}px`);
    });
  }
};
const provideColors = (el, colors) => {
  Object.keys(colors).forEach((color) => {
    const colorValue = colors[color];
    if (typeof colorValue === 'string') {
      if (color === 'text' || color === 'text-on-brand') {
        const rgb = hexToRGB(colorValue).join(' ');
        el.style.setProperty(`--dyte-colors-${color}-1000`, `${rgb}`);
        el.style.setProperty(`--dyte-colors-${color}-900`, `${rgb} / 0.88`);
        el.style.setProperty(`--dyte-colors-${color}-800`, `${rgb} / 0.76`);
        el.style.setProperty(`--dyte-colors-${color}-700`, `${rgb} / 0.64`);
        el.style.setProperty(`--dyte-colors-${color}-600`, `${rgb} / 0.52`);
      }
      else {
        const rgb = hexToRGB(colorValue).join(' ');
        el.style.setProperty(`--dyte-colors-${color}`, rgb);
      }
    }
    else if (typeof colorValue === 'object') {
      Object.keys(colorValue).forEach((shade) => {
        const shadeValue = colorValue[shade];
        const rgb = hexToRGB(shadeValue).join(' ');
        el.style.setProperty(`--dyte-colors-${color}-${shade}`, rgb);
      });
    }
  });
};
/**
 * Provides the design system new tokens to consume values from for styling the Dyte UI Kit's UI.
 * @param el The element/node you want to _provide_ Dyte's design system.
 * @param tokens The design tokens you want to updated.
 */
const provideDyteDesignSystem = (el, { spacingBase, borderRadius, borderWidth, colors, fontFamily, googleFont, theme }) => {
  if (typeof el !== 'object') {
    throw new Error('[dyte-ui-kit] (provideDyteDesignSystem): Passed element is not a valid HTML Element');
  }
  if (typeof googleFont === 'string') {
    provideGoogleFont(googleFont);
    el.style.setProperty('--dyte-font-family', googleFont);
  }
  if (typeof fontFamily === 'string') {
    el.style.setProperty('--dyte-font-family', fontFamily);
  }
  if (typeof spacingBase === 'number') {
    provideSpacing(el, spacingBase);
  }
  if (typeof borderWidth === 'string') {
    provideBorderWidth(el, borderWidth);
  }
  if (typeof borderRadius === 'string') {
    provideBorderRadius(el, borderRadius);
  }
  if (typeof theme === 'string') {
    const colors = THEMES[theme];
    if (colors != null)
      provideColors(el, colors);
  }
  if (typeof colors === 'object') {
    provideColors(el, colors);
  }
};

/**
 * Extend the default UI Config with your own
 * @param config Your extended UI Config
 * @returns New extended UI Config object
 */
const extendConfig = (config, baseConfig = defaultConfig) => {
  let newConfig = Object.assign({}, baseConfig);
  lodash_merge(newConfig, config);
  return newConfig;
};
/**
 * Generates a config with older theme value.
 * @param oldConfig V1 Theme object
 * @param toExtend UI Config object to extend the generated config
 * @param options Options for toggling components
 * @returns
 */
const generateConfig = (oldConfig, toExtend = {}, options = { grid_pagination: true, settings_toggle: true }) => {
  var _a, _b, _c, _d;
  const data = { showSetupScreen: true };
  let logo;
  let meetingElements = ['dyte-stage'];
  let headerChildren = {}, controlBarChildren = {};
  const showSettingsToggle = (options === null || options === void 0 ? void 0 : options.settings_toggle) !== false;
  const showGridPagination = (options === null || options === void 0 ? void 0 : options.grid_pagination) !== false;
  if ((_a = oldConfig.controlBar) === null || _a === void 0 ? void 0 : _a.isEnabled) {
    meetingElements.push('dyte-controlbar');
    const { elements } = oldConfig.controlBar;
    const leftElements = ['dyte-breakout-rooms-toggle'], rightElements = [];
    const centerElements = [
      'dyte-mic-toggle',
      'dyte-camera-toggle',
      'dyte-webinar-stage-toggle',
      ...(showSettingsToggle ? ['dyte-settings-toggle'] : []),
      'dyte-leave-button',
    ];
    if (elements.fullscreen) {
      leftElements.push('dyte-fullscreen-toggle');
    }
    if (elements.screenshare) {
      leftElements.push('dyte-screen-share-toggle');
    }
    // NOTE(callmetarush): No condition as permission check happens
    // within component
    leftElements.push('dyte-recording-toggle');
    if (elements.chat) {
      rightElements.push('dyte-chat-toggle');
    }
    if (elements.polls) {
      rightElements.push('dyte-polls-toggle');
    }
    if (elements.participants) {
      rightElements.push('dyte-participants-toggle');
    }
    if (elements.plugins) {
      rightElements.push('dyte-plugins-toggle');
    }
    const allSideElements = leftElements.concat(rightElements);
    if (showSettingsToggle) {
      allSideElements.push('dyte-settings-toggle');
    }
    let hasMobileDrawer = false;
    if (allSideElements.length > 0) {
      hasMobileDrawer = true;
    }
    controlBarChildren = {
      'dyte-controlbar': {
        states: ['activeMoreMenu'],
        children: ['div#controlbar-left', 'div#controlbar-center', 'div#controlbar-right'],
      },
      'div#controlbar-left': leftElements,
      'div#controlbar-center': centerElements,
      'div#controlbar-right': rightElements,
      'dyte-controlbar.sm': ['div#controlbar-mobile'],
      'dyte-controlbar.md': ['div#controlbar-mobile'],
      'dyte-controlbar.activeMoreMenu.sm': ['div#controlbar-mobile', 'div#more-menu'],
      'dyte-controlbar.activeMoreMenu.md': ['div#controlbar-mobile', 'div#more-menu'],
      'div#controlbar-mobile': [
        'dyte-mic-toggle',
        'dyte-camera-toggle',
        'dyte-webinar-stage-toggle',
        'dyte-leave-button',
        ...[hasMobileDrawer && 'dyte-more-toggle'],
      ],
      'div#more-menu': allSideElements.map((el) => [el, { variant: 'horizontal' }]),
    };
  }
  if ((_b = oldConfig.header) === null || _b === void 0 ? void 0 : _b.isEnabled) {
    meetingElements.unshift('dyte-header');
    const { elements } = oldConfig.header;
    let leftElements = ['dyte-recording-indicator'], centerElements = [], rightElements = [];
    if (showGridPagination) {
      rightElements.push('dyte-grid-pagination');
    }
    if (elements.title) {
      centerElements.push('dyte-meeting-title');
    }
    if (typeof elements.logo === 'string' && elements.logo.length > 0) {
      logo = elements.logo;
      leftElements.unshift('dyte-logo');
    }
    if (elements.participantCount) {
      rightElements.push('dyte-participant-count');
    }
    if (elements.timer) {
      rightElements.push('dyte-clock');
    }
    headerChildren = {
      'dyte-header': ['div#header-left', 'div#header-center', 'div#header-right'],
      'dyte-header.sm': ['div#header-left', 'div#header-right'],
      'div#header-left': leftElements,
      'div#header-center': centerElements,
      'div#header-right': rightElements,
      // do not use dyte-logo in sm breakpoint
      'div#header-left.sm': leftElements.slice(1).concat(['dyte-meeting-title']),
    };
  }
  meetingElements.push('dyte-participants-audio', 'dyte-dialog-manager');
  const designTokens = {
    logo,
  };
  if (oldConfig.colors != null) {
    designTokens['colors'] = {};
    if (isValidHexColor(oldConfig.colors.primary)) {
      designTokens['colors']['brand'] = getBrandColors(oldConfig.colors.primary);
    }
    else if (typeof oldConfig.colors.primary === 'object') {
      designTokens['colors']['brand'] = oldConfig.colors.primary;
    }
    if (isValidHexColor(oldConfig.colors.secondary)) {
      designTokens['colors']['background'] = getBackgroundColorsOld(oldConfig.colors.secondary);
    }
    else if (typeof oldConfig.colors.secondary === 'object') {
      designTokens['colors']['background'] = oldConfig.colors.secondary;
    }
    if (isValidHexColor(oldConfig.colors.text)) {
      designTokens['colors']['text'] = oldConfig.colors.text;
    }
    if (isValidHexColor(oldConfig.colors.textPrimary)) {
      designTokens['colors']['text-on-brand'] = oldConfig.colors.textPrimary;
    }
    if (isValidHexColor(oldConfig.colors.videoBackground)) {
      designTokens['colors']['video-bg'] = oldConfig.colors.videoBackground;
    }
  }
  let config = {
    designTokens,
    styles: {
      'dyte-header': {
        display: 'grid',
        height: '48px',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gridTemplateRows: '1fr',
        alignItems: 'center',
      },
      'dyte-header.sm': {
        display: 'grid',
        gridArea: 'header',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridTemplateRows: '1fr',
        alignItems: 'center',
      },
      'div#header-left': {
        display: 'flex',
        alignItems: 'center',
        height: '48px',
      },
      'dyte-logo': {
        height: '26px',
      },
      'div#header-center': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      'div#header-right': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      },
      'dyte-stage': {
        display: 'flex',
        flex: '1',
      },
      'dyte-grid': {
        flex: '1',
        height: 'auto',
      },
      'dyte-controlbar': {
        display: 'grid',
        height: '76px',
        gridTemplateColumns: 'repeat(3,1fr)',
        gridTemplateRows: '1fr',
        alignItems: 'center',
        padding: '8px',
      },
      'dyte-controlbar.sm': {
        display: 'flex',
        position: 'relative',
        backgroundColor: 'var(--dyte-colors-background-1000, #000)',
      },
      'dyte-controlbar.md': {
        display: 'flex',
        position: 'relative',
        backgroundColor: 'var(--dyte-colors-background-1000, #000)',
      },
      'div#controlbar-left': {
        display: 'flex',
        alignItems: 'center',
      },
      'div#controlbar-center': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      'div#controlbar-mobile': {
        display: 'flex',
        flex: '1',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: '10000',
      },
      'div#controlbar-right': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      },
      'dyte-settings': {
        width: '720px',
        height: '480px',
      },
      'div#more-menu': {
        position: 'absolute',
        boxSizing: 'border-box',
        top: '0',
        left: '0',
        width: '100%',
        transform: 'translateY(-100%)',
        backgroundColor: 'rgb(var(--dyte-colors-background-1000, 0 0 0))',
        padding: '16px 12px',
        borderRadius: '18px 18px 0 0',
        zIndex: '10000',
      },
      'div#setupcontrols-media': {
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        display: 'flex',
      },
      'div#setupcontrols-settings': {
        position: 'absolute',
        top: '8px',
        right: '8px',
      },
      'dyte-meeting-title.sm': {
        marginLeft: '0',
      },
      'dyte-clock': {
        marginRight: '0',
      },
    },
    root: Object.assign(Object.assign(Object.assign({ 'dyte-meeting': {
        // if using key value pair, provide the key in `state`
        // else provide array of states in `states`
        state: 'meeting',
        states: ['activeSidebar'],
      }, 'dyte-meeting[meeting=idle]': ['dyte-idle-screen'], 'dyte-meeting[meeting=waiting]': ['dyte-waiting-screen'], 'dyte-meeting[meeting=setup]': ['dyte-setup-screen', 'dyte-dialog-manager'], 'dyte-meeting[meeting=joined]': meetingElements, 'dyte-meeting[meeting=joined].activeSidebar.sm': [
        ...meetingElements,
        ['dyte-sidebar', { view: 'full-screen' }],
      ], 'dyte-meeting[meeting=joined].activeSidebar.md': [
        ...meetingElements,
        ['dyte-sidebar', { view: 'full-screen' }],
      ], 'dyte-meeting[meeting=ended]': ['dyte-ended-screen'] }, headerChildren), controlBarChildren), { 'dyte-stage': {
        states: ['activeSidebar', 'activeBreakoutRoomGrid'],
        children: ['dyte-grid', 'dyte-notifications', 'dyte-spotlight-indicator'],
      }, 'dyte-stage.activeBreakoutRoomGrid': [
        'dyte-breakout-room-grid',
        'dyte-notifications',
        'dyte-sidebar',
      ], 'dyte-stage.activeBreakoutRoomGrid.activeSidebar': [
        'dyte-breakout-room-grid',
        'dyte-notifications',
        ['dyte-sidebar', { view: 'sidebar' }],
      ], 'dyte-stage.activeSidebar': [
        'dyte-grid',
        'dyte-notifications',
        'dyte-spotlight-indicator',
        ['dyte-sidebar', { view: 'sidebar' }],
      ], 
      // hide sidebar for smaller screens
      'dyte-stage.activeSidebar.sm': [
        'dyte-grid',
        'dyte-notifications',
        'dyte-spotlight-indicator',
      ], 'dyte-grid': {
        states: ['activeScreenShare', 'activePlugin', 'activeSpotlight'],
        children: ['dyte-simple-grid'],
      }, 'dyte-grid.activeSpotlight': ['dyte-spotlight-grid'], 'dyte-grid.activeScreenShare': ['dyte-mixed-grid'], 'dyte-grid.activePlugin': ['dyte-mixed-grid'], 'dyte-grid.activeScreenShare.activeSpotlight': ['dyte-mixed-grid'], 'dyte-grid.activePlugin.activeSpotlight': ['dyte-mixed-grid'], 'dyte-grid.activePlugin.activeScreenShare.activeSpotlight': ['dyte-mixed-grid'], 'dyte-mixed-grid': {
        states: ['activeSpotlight'],
        children: ['dyte-simple-grid'],
      }, 'dyte-mixed-grid.activeSpotlight': ['dyte-spotlight-grid'], 'dyte-participant-tile': {
        state: 'meeting',
        children: ['dyte-name-tag', 'dyte-avatar'],
      }, 'dyte-participant-setup': {
        children: ['dyte-avatar', 'div#setupcontrols-media'],
      }, 'dyte-participant-tile[meeting=setup]': {
        children: [
          'dyte-avatar',
          'div#setupcontrols-media',
          ...(showSettingsToggle ? ['div#setupcontrols-settings'] : []),
        ],
      }, 'div#setupcontrols-media': [
        ['dyte-mic-toggle', { size: 'sm' }],
        ['dyte-camera-toggle', { size: 'sm' }],
      ], 'div#setupcontrols-settings': [['dyte-settings-toggle', { size: 'sm' }]], 'dyte-screenshare-view': {
        children: ['dyte-name-tag'],
      }, 'dyte-name-tag': [['dyte-audio-visualizer', { slot: 'start' }]] }),
    config: {
      notification_sounds: {
        participant_left: false,
      },
      participant_joined_sound_notification_limit: 3,
      participant_chat_message_sound_notification_limit: 10,
      videoFit: 'cover',
    },
  };
  config = extendConfig(toExtend, config);
  data.showSetupScreen = (_d = (_c = oldConfig.setupScreen) === null || _c === void 0 ? void 0 : _c.isEnabled) !== null && _d !== void 0 ? _d : true;
  return { config, data };
};

export { extendConfig as e, generateConfig as g, provideDyteDesignSystem as p };
